import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { isFulfilled } from "@reduxjs/toolkit";

import CancelIcon from "assets/icons/CancelIcon";
import LoadingIcon from "assets/icons/LoadingIcon";
import ArrowLeftIcon from "assets/icons/ArrowLeftIcon";
import ArrowRightIcon from "assets/icons/ArrowRightIcon";
import Portal from "components/Portal";
import LikeIcon from "assets/icons/LikeIcon";
import DownloadIcon from "assets/icons/DownloadIcon";
import type { Picture } from "types/Pictures";
import useAllClasses from "hooks/useAllClasses";
import { formatDate } from "utilities/DateFormatter";
import { PicturePublishStatus, SupportedLang } from "config/constants";
import TagStudentList from "./TagStudentList";
import DeletePromptDialog from "./DeletePromptDialog";
import useAppDispatch from "hooks/useAppDispatch";
import {
  updatePicturePublishStatus,
  updateIsFavoriteStatus,
} from "features/picture/pictureAction";
import { setErrorStatus } from "features/error/errorSlice";
import { setSuccessStatus } from "features/success/successSlice";
import { FaBookmark } from "react-icons/fa";
import { CiBookmark } from "react-icons/ci";
import { AiOutlineCloseCircle } from "react-icons/ai";
import ActivityTagContainer from "../../components/UploadPicture/ActivityTagContainer";

interface PictureDialogProps {
  picture: Picture;
  onPrevious: (() => void) | null;
  onNext: (() => void) | null;
  onClose: () => void;
}

const PictureDialog: React.FC<PictureDialogProps> = ({
  picture,
  onPrevious,
  onNext,
  onClose,
}) => {
  const { t } = useTranslation();
  const { classes } = useAllClasses();
  const [isLoading, setIsLoading] = useState(false);
  const [hoveredDirection, setHoveredDirection] = useState<
    "previous" | "next" | null
  >(null);

  useEffect(() => {
    const image = new Image();
    image.src = picture.original_picture;
    setIsLoading(!image.complete);

    return () => {
      setIsLoading(true);
    };
  }, [picture]);

  const downloadPicture = () => {
    const link = document.createElement("a");
    link.href = picture.original_picture;
    link.download = `${picture.id}_${Date.now()}.jpg`;
    link.click();
  };

  const [localIsFavorite, setLocalIsFavorite] = useState(picture.is_favorite);

  // Update local state when picture prop changes
  useEffect(() => {
    setLocalIsFavorite(picture.is_favorite);
  }, [picture.is_favorite]);

  const updateIsFavorite = async () => {
    // Update local state immediately for responsive UI
    setLocalIsFavorite(!localIsFavorite);

    const updateAction = await dispatch(
      updateIsFavoriteStatus({
        id: picture.id,
        formData: { is_favorite: !localIsFavorite },
      }),
    );
    if (!isFulfilled(updateAction)) {
      // Revert on failure
      setLocalIsFavorite(localIsFavorite);
      dispatch(setErrorStatus(""));
    }
  };

  const [isShowDeleteDialog, setIsShowDeleteDialog] = useState(false);
  const dispatch = useAppDispatch();
  const deletePicture = async () => {
    setIsShowDeleteDialog(false);
    const updateAction = await dispatch(
      updatePicturePublishStatus({
        id: picture.id,
        formData: { public_status: PicturePublishStatus.deleted },
      }),
    );
    if (!isFulfilled(updateAction)) {
      dispatch(setErrorStatus(""));
    } else {
      dispatch(
        setSuccessStatus({
          title: "_published_picture._delete_success",
          message: "_published_picture._delete_success_content",
          isDeleted: true,
        }),
      );
    }
  };

  const renderClassName = (classId: number) => {
    const currentClass = classes.find((c) => c.id === classId);
    if (!currentClass) return "";
    return `${currentClass.year} - ${currentClass.name}`;
  };

  return (
    <Portal>
      <div className="fixed z-[99] top-0 left-0 w-full h-full bg-black/50 flex justify-center items-center p-3 sm:p-4">
        <div className="w-full h-full max-h-[calc(100vh-1.5rem)] max-w-[calc(100vw-1.5rem)] sm:max-h-[calc(100vh-2rem)] sm:max-w-[calc(100vw-2rem)] bg-white z-[100] rounded-lg overflow-hidden">
          <div className="h-full max-h-full overflow-y-auto lg:overflow-hidden lg:flex justify-between">
          <div className="lg:hidden w-full h-12 flex flex-row-reverse justify-between items-center px-4 py-2 border-b border-gray-200">
            <button className="cursor-pointer p-1" onClick={onClose}>
              <CancelIcon width={24} height={24} color={"black"} />
            </button>
          </div>
          <div className="lg:w-[70%] h-[60vh] lg:h-auto bg-[#fcfcfc] flex flex-col justify-center border-e border-e-[#eee] relative">
            {isLoading && (
              <div className="absolute top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%]">
                <div className="w-24 sm:w-32">
                  <LoadingIcon />
                </div>
              </div>
            )}
            <div className="absolute right-4 top-4 z-20">
              <button
                onClick={updateIsFavorite}
                className="rounded-full bg-white p-1.5 sm:p-2 shadow-md hover:bg-gray-100 transition-colors duration-200 ease-out relative group"
              >
                {localIsFavorite ? (
                  <FaBookmark className="text-[#EA6E7D]" size={20} />
                ) : (
                  <CiBookmark className="text-[#767676]" size={20} />
                )}
                {/* Tooltip */}
                <div className="absolute invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-opacity duration-200 -bottom-10 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs rounded py-1 px-2 w-max">
                  <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 rotate-45 w-2 h-2 bg-gray-800"></div>
                  {t("_published_picture._favorite")}
                </div>
              </button>
            </div>
            <img
              className="max-w-full max-h-full object-contain m-auto pointer-events-auto"
              src={picture.original_picture}
              alt=""
              style={{
                visibility: !isLoading ? "visible" : "hidden",
              }}
              onLoad={() => setIsLoading(false)}
            />
            <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
              {onPrevious && (
                <div className="absolute top-[50%] left-2 sm:left-5 z-20 pointer-events-auto">
                  <button
                    className="rounded-full pointer-events-auto p-1 sm:p-2 transition-colors duration-200 ease-out"
                    style={{
                      backgroundColor:
                        hoveredDirection === "previous"
                          ? "rgba(0, 0, 0, 0.7)"
                          : "rgba(0, 0, 0, 0.5)",
                    }}
                    onClick={onPrevious}
                    onMouseEnter={() => setHoveredDirection("previous")}
                    onMouseLeave={() => setHoveredDirection(null)}
                  >
                    <ArrowLeftIcon width={24} height={24} color={"white"} />
                  </button>
                </div>
              )}
              {onNext && (
                <div className="absolute top-[50%] right-2 sm:right-5 z-20 pointer-events-auto">
                  <button
                    className="rounded-full pointer-events-auto p-1 sm:p-2 transition-colors duration-200 ease-out"
                    style={{
                      backgroundColor:
                        hoveredDirection === "next"
                          ? "rgba(0, 0, 0, 0.7)"
                          : "rgba(0, 0, 0, 0.5)",
                    }}
                    onClick={onNext}
                    onMouseEnter={() => setHoveredDirection("next")}
                    onMouseLeave={() => setHoveredDirection(null)}
                  >
                    <ArrowRightIcon width={24} height={24} color={"white"} />
                  </button>
                </div>
              )}
            </div>
          </div>
          <div className="lg:w-[30%] h-auto lg:h-auto py-2 px-2 sm:px-4 space-y-3 lg:space-y-5 flex flex-col justify-between overflow-x-hidden">
            <div className="space-y-3 lg:space-y-5">
              <div className="hidden w-full lg:flex flex-row-reverse justify-between items-center">
                <div className="cursor-pointer" onClick={onClose}>
                  <CancelIcon width={32} height={32} color={"black"} />
                </div>
              </div>
              <div className="space-y-1 lg:space-y-2">
                <h2 className="text-base sm:text-lg lg:text-2xl font-medium">
                  {renderClassName(picture.class_id)}
                </h2>
                <p className="text-xs sm:text-sm lg:text-base">
                  {t("_published_picture._published_date")}
                  {formatDate(new Date(picture.publish_time), {
                    [SupportedLang.ja]: "yo Mo do",
                    [SupportedLang.en]: "MMMM d, yyyy",
                  })}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <LikeIcon width={20} height={20} color={"#767676"} />
                <span className="text-xs sm:text-sm lg:text-base">
                  {picture.total_favorite} likes
                </span>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <button
                    className="flex items-center bg-primary text-white p-2 rounded-lg gap-2 transition-colors duration-200 ease-out hover:bg-primary/90"
                    onClick={downloadPicture}
                  >
                    <DownloadIcon width={24} height={24} color={"#ffffff"} />
                    <p className="text-sm sm:text-base">
                      {t("_published_picture._download")}
                    </p>
                  </button>
                </div>
                <div className="flex justify-end">
                  <button
                    className="flex items-center gap-1 text-[#ff0000] text-sm sm:text-base hover:underline"
                    onClick={() => setIsShowDeleteDialog(true)}
                  >
                    <AiOutlineCloseCircle size={14} />
                    {t("_published_picture._delete")}
                  </button>
                </div>
              </div>
            </div>
            <div
              id="tag-students"
              className="flex-1 min-h-[80px] sm:min-h-[60px] border border-[#9B9B9B] p-2"
            >
              <TagStudentList picture={picture} />
            </div>
            <ActivityTagContainer pictures={[picture]} />
          </div>
        </div>
        {isShowDeleteDialog && (
          <DeletePromptDialog
            onNoClick={() => setIsShowDeleteDialog(false)}
            onYesClick={deletePicture}
          />
        )}
      </div>
      </div>
    </Portal>
  );
};

export default PictureDialog;
